import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import DeviceInfo from 'react-native-device-info';

import { scale } from '@components/frame';
import { isWeb, setSecureValue } from '@helpers';

import LineButton from '@components/common/lineButton';
import TouchableElement from '@components/common/touchableElement';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { clear, changePassword } from '@reducers/user';
import { deleteDevice } from '@reducers/device';
import { unlogin } from '@reducers/utils';

import { SmallLoading } from '@components/loading';
import UserImage from '@components/common/UserImage';

const AccountLine = ({ item: { userInfo }, onPress, borderTop, roundTop, roundBottom, C, accountLoading, check }) => {
  const { companyProfile = {}, emailAddress = ' ' } = userInfo;
  return (
    <TouchableElement onPress={check ? null : onPress} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}>
      {({ boxStyle, pressHoverColor }) => (
        <View
          style={[
            boxStyle,
            { paddingLeft: scale(15), backgroundColor: C(pressHoverColor('Background/Light/Field')), height: scale(54) },
            roundTop && {
              borderTopLeftRadius: scale(10),
              borderTopRightRadius: scale(10),
            },
            roundBottom && {
              borderBottomLeftRadius: scale(10),
              borderBottomRightRadius: scale(10),
            },
          ]}
        >
          <View
            style={[
              {
                paddingRight: scale(15),
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignContent: 'center',
                alignItems: 'center',
                height: '100%',
              },
              borderTop && {
                borderTopColor: C('Hairline/Light/No trans'),
                borderTopWidth: StyleSheet.hairlineWidth,
              },
            ]}
          >
            <View>
              <Text style={[font['Body'], { color: C('Label/Light/Primary') }]}>{companyProfile.companyName}</Text>
              <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginTop: scale(2) }]}>{emailAddress}</Text>
            </View>
            <View style={{ opacity: check ? 1 : 0 }}>
              {accountLoading === emailAddress ? (
                <SmallLoading />
              ) : (
                <Svg icon="check" color="Selected/Light/Primary" width={scale(isWeb ? 15 : 14)} height={scale(isWeb ? 15 : 14)} />
              )}
            </View>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

const Profile = (props) => {
  const {
    C,
    user: { userInfo = {} },
    changePassword,
    setEditFieldConfig,
    toggleEditField,
    deleteDevice,
    unlogin,
    toggleShowNewAccount,
    accounts,
    selectNewUser,
    accountLoading,
    clear,
    setAccounts,
  } = props;

  const [cleaning, setCleaning] = useState(false);
  const [cleaningAll, setCleaningAll] = useState(false);

  const { userProfile = {}, companyProfile = {}, emailAddress = ' ' } = userInfo;
  const { firstName = ' ', lastName = ' ', userId } = userProfile;

  const logOff = async () => {
    setCleaning(true);
    if (isWeb) {
      // Always fully log out this tab. unlogin() calls logoutRedirect({ account })
      // which only removes THIS account from MSAL's shared cache — other tabs'
      // accounts are preserved.
      unlogin();
    } else {
      if (accounts.length === 1) {
        const deviceId = await DeviceInfo.getUniqueId();
        if (deviceId) {
          try {
            await deleteDevice(deviceId);
          } catch (e) {}
        }
        try {
          clear();
        } catch (e) {}
        unlogin();
      } else {
        const newAccounts = accounts.filter((item) => item.userInfo.emailAddress !== emailAddress);
        setAccounts(newAccounts);
        setSecureValue('accounts', JSON.stringify(newAccounts));
        selectNewUser(newAccounts[0]);
      }
    }
    setCleaning(false);
  };

  const logOffAllAccouts = async () => {
    setCleaningAll(true);
    if (!isWeb) {
      const deviceId = await DeviceInfo.getUniqueId();
      if (deviceId) {
        deleteDevice(deviceId);
      }
    }
    clear();
    unlogin({ clearAll: true });
    setCleaningAll(false);
  };

  return (
    <View>
      <View>
        <View style={{ width: '100%', alignItems: 'center', marginTop: scale(18) }}>
          <UserImage {...{ showAdd: true }} />
        </View>
        <View style={{ alignItems: 'center' }}>
          <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), marginTop: scale(12) }]}>
            {firstName} {lastName}
          </Text>
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), marginTop: scale(3) }]}>{emailAddress}</Text>
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), marginTop: scale(4) }]}>{companyProfile.companyName}</Text>
        </View>
      </View>
      <View
        style={[
          {
            width: '100%',
            marginTop: scale(28),
          },
        ]}
      >
        <LineButton
          {...{
            onPress: () => {
              setEditFieldConfig({
                header: 'Change Password',
                initData: {},
                submitDisabled: ({ oldPassword, newPassword, confirmedPassword }) => {
                  return !oldPassword || !newPassword || !confirmedPassword || newPassword !== confirmedPassword;
                },
                onSubmit: async (data, onClose) => {
                  await changePassword(data, () => {
                    onClose();
                  });
                },
                inputs: [
                  {
                    key: 'oldPassword',
                    name: 'Old Password',
                    noArrow: true,
                    lableWidth: scale(110),
                    placeholder: 'Old Password',
                    secure: true,
                  },
                  {
                    key: 'newPassword',
                    name: 'New Password',
                    noArrow: true,
                    lableWidth: scale(110),
                    placeholder: 'New Password',
                    secure: true,
                  },
                  {
                    key: 'confirmedPassword',
                    name: 'Confirm New Password',
                    noArrow: true,
                    lableWidth: scale(110),
                    placeholder: 'Confirm New Password',
                    secure: true,
                  },
                ],
              });
              toggleEditField();
            },
            text: 'Change Password',
            color: 'Link/Light/Primary',
            C,
          }}
        />
      </View>
      <View
        style={[
          {
            width: '100%',
            marginTop: scale(30),
          },
        ]}
      >
        <LineButton
          {...{
            onPress: logOff,
            loading: cleaning,
            text: 'Log Off',
            color: 'Chart/Light/Warning',
            C,
          }}
        />
      </View>
      {accounts && accounts.length > 1 && (
        <View>
          <View style={{ width: '100%', paddingHorizontal: scale(15), marginTop: scale(28) }}>
            <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('YOUR ACCOUNTS')}</Text>
          </View>
          <View
            style={[
              {
                width: '100%',
                marginTop: scale(5),
              },
            ]}
          >
            {accounts.map((item, index) =>
              item && item.userInfo ? (
                <AccountLine
                  key={item.userInfo.emailAddress}
                  {...{
                    accountLoading,
                    onPress: () => {
                      if (accountLoading) {
                        return;
                      }
                      selectNewUser(item);
                    },
                    check: item.userInfo.emailAddress === emailAddress,
                    borderTop: index !== 0,
                    roundTop: index === 0,
                    roundBottom: index === accounts.length - 1,
                    item,
                    index,
                    C,
                  }}
                />
              ) : null
            )}
          </View>
          <View
            style={[
              {
                width: '100%',
                marginTop: scale(30),
              },
            ]}
          >
            <LineButton
              {...{
                onPress: logOffAllAccouts,
                loading: cleaningAll,
                text: 'Log Off All Accounts',
                color: 'Chart/Light/Warning',
                C,
              }}
            />
          </View>
        </View>
      )}
      {(
        <View
          style={[
            {
              width: '100%',
              marginTop: scale(30),
            },
          ]}
        >
          <LineButton {...{ onPress: toggleShowNewAccount, text: 'Add Account', color: 'Link/Light/Primary', C }} />
          <View style={{ width: '100%', paddingHorizontal: scale(15), marginTop: scale(6), marginBottom: scale(20) }}>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
              {i18n.t(
                'You can add another account of another organization You cannot use multiple accounts of the same organization on the same device'
              )}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};
export default connect(({ user }) => ({ user }), {
  changePassword,
  deleteDevice,
  clear,
  unlogin,
})(Profile);
